/**
 * Presentation-agnostic FORM SPECS for each calculator: which fields to show,
 * their options, and how to turn form values into displayable result rows.
 *
 * This is still pure data + functions — no React, no platform. It lives in the
 * engine on purpose: the app and web both render their UI from this ONE spec,
 * so they show the same inputs AND the same formatted numbers. Divergence
 * between free web and paid app is the #1 trust killer (design doc §0); this
 * closes it at the source. Labels are English (v1 is English-first); a UI may
 * map `field.key` to its own i18n if it wants.
 */
import { voltageDrop } from "./calculators/voltageDrop.ts";
import { conduitFill } from "./calculators/conduitFill.ts";
import { ampacity } from "./calculators/ampacity.ts";
import { boxFill } from "./calculators/boxFill.ts";
import { conduitBending } from "./calculators/conduitBending.ts";
import { AWG_SIZES } from "./types.ts";
import { CONDUIT_TYPE_LABELS, TRADE_SIZES } from "./tables/conduit.ts";
import { BOX_FILL_AWG_SIZES } from "./tables/boxFill.ts";
import { OFFSET_ANGLES } from "./tables/bending.ts";
import type { CalculatorId } from "./catalog.ts";

export type ResultRow = { label: string; value: string; warn?: boolean };

export type FormResult =
  | { ok: true; headline: ResultRow; rows: ResultRow[] }
  | { ok: false; error: string };

export type FormField =
  | {
      key: string;
      label: string;
      kind: "number";
      unit?: string;
      default: string;
      step?: string;
    }
  | {
      key: string;
      label: string;
      kind: "select";
      options: { value: string; label: string }[];
      default: string;
    };

export type FormSpec = {
  fields: FormField[];
  compute: (values: Record<string, string>) => FormResult;
};

const num = (s: string | undefined): number => Number(s);
const fmt = (n: number, digits = 2): string =>
  Number.isFinite(n) ? n.toFixed(digits).replace(/\.?0+$/, "") || "0" : "—";

const awgOptions = AWG_SIZES.map((a) => ({ value: a, label: `${a} AWG` }));
const conduitOptions = (
  Object.keys(CONDUIT_TYPE_LABELS) as (keyof typeof CONDUIT_TYPE_LABELS)[]
).map((k) => ({ value: k, label: CONDUIT_TYPE_LABELS[k] }));
const tradeOptions = TRADE_SIZES.map((t) => ({ value: t, label: `${t}"` }));
const materialOptions = [
  { value: "copper", label: "Copper" },
  { value: "aluminum", label: "Aluminum" },
];
const boxAwgOptions = BOX_FILL_AWG_SIZES.map((a) => ({ value: a, label: `${a} AWG` }));

export const CALC_FORMS: Record<CalculatorId, FormSpec> = {
  voltageDrop: {
    fields: [
      { key: "sourceVolts", label: "Source voltage", unit: "V", kind: "number", default: "120" },
      { key: "currentA", label: "Load current", unit: "A", kind: "number", default: "20" },
      { key: "lengthFt", label: "One-way length", unit: "ft", kind: "number", default: "100" },
      { key: "awg", label: "Conductor size", kind: "select", options: awgOptions, default: "12" },
      { key: "material", label: "Material", kind: "select", options: materialOptions, default: "copper" },
      {
        key: "phase",
        label: "Phase",
        kind: "select",
        options: [
          { value: "single", label: "Single phase" },
          { value: "three", label: "Three phase" },
        ],
        default: "single",
      },
    ],
    compute: (v) => {
      const r = voltageDrop({
        sourceVolts: num(v.sourceVolts),
        currentA: num(v.currentA),
        lengthFt: num(v.lengthFt),
        awg: v.awg as never,
        material: v.material as never,
        phase: v.phase as never,
      });
      if (!r.ok) return r;
      const over = r.value.percentDrop > 3;
      return {
        ok: true,
        headline: { label: "Voltage drop", value: `${fmt(r.value.voltageDropV)} V` },
        rows: [
          { label: "% of source", value: `${fmt(r.value.percentDrop)} %`, warn: over },
          { label: "Voltage at load", value: `${fmt(r.value.endVoltageV)} V` },
          { label: "Resistance", value: `${fmt(r.value.conductorResistanceOhm, 4)} Ω` },
          ...(over
            ? [{ label: "Note", value: "Exceeds 3% guideline", warn: true } as ResultRow]
            : []),
        ],
      };
    },
  },

  conduitFill: {
    fields: [
      { key: "conduitType", label: "Conduit type", kind: "select", options: conduitOptions, default: "EMT" },
      { key: "tradeSize", label: "Trade size", kind: "select", options: tradeOptions, default: "1/2" },
      { key: "awg", label: "Conductor size", kind: "select", options: awgOptions, default: "12" },
      {
        key: "insulation",
        label: "Insulation",
        kind: "select",
        options: [
          { value: "THHN", label: "THHN/THWN-2" },
          { value: "XHHW", label: "XHHW" },
        ],
        default: "THHN",
      },
      { key: "count", label: "Number of conductors", kind: "number", default: "3", step: "1" },
    ],
    compute: (v) => {
      const r = conduitFill({
        conduitType: v.conduitType as never,
        tradeSize: v.tradeSize as never,
        conductors: [{ awg: v.awg as never, insulation: v.insulation as never, count: num(v.count) }],
      });
      if (!r.ok) return r;
      return {
        ok: true,
        headline: { label: "Fill", value: `${fmt(r.value.actualFillPercent)} %`, warn: r.value.overfilled },
        rows: [
          { label: "Allowable fill", value: `${fmt(r.value.allowableFillPercent)} %` },
          { label: "Conductor area", value: `${fmt(r.value.totalConductorAreaIn2, 4)} in²` },
          { label: "Conduit area", value: `${fmt(r.value.conduitAreaIn2, 3)} in²` },
          {
            label: "Status",
            value: r.value.overfilled ? "OVER FILL — size up" : "Within NEC limit",
            warn: r.value.overfilled,
          },
        ],
      };
    },
  },

  ampacity: {
    fields: [
      { key: "awg", label: "Conductor size", kind: "select", options: awgOptions, default: "6" },
      { key: "material", label: "Material", kind: "select", options: materialOptions, default: "copper" },
      {
        key: "tempRating",
        label: "Insulation rating",
        kind: "select",
        options: [
          { value: "60C", label: "60°C (TW)" },
          { value: "75C", label: "75°C (THW/THWN)" },
          { value: "90C", label: "90°C (THHN)" },
        ],
        default: "75C",
      },
      { key: "ambientC", label: "Ambient temp", unit: "°C", kind: "number", default: "30" },
      {
        key: "currentCarryingConductors",
        label: "Current-carrying conductors",
        kind: "number",
        default: "3",
        step: "1",
      },
    ],
    compute: (v) => {
      const r = ampacity({
        awg: v.awg as never,
        material: v.material as never,
        tempRating: v.tempRating as never,
        ambientC: num(v.ambientC),
        currentCarryingConductors: num(v.currentCarryingConductors),
      });
      if (!r.ok) return r;
      return {
        ok: true,
        headline: { label: "Derated ampacity", value: `${fmt(r.value.deratedAmpacityA)} A` },
        rows: [
          { label: "Base (310.16)", value: `${fmt(r.value.baseAmpacityA)} A` },
          { label: "Temp correction", value: `× ${fmt(r.value.tempCorrectionFactor, 3)}` },
          { label: "Bundling", value: `× ${fmt(r.value.adjustmentFactor, 3)}` },
        ],
      };
    },
  },

  boxFill: {
    fields: [
      { key: "boxVolumeIn3", label: "Box volume", unit: "in³", kind: "number", default: "18" },
      { key: "awg", label: "Conductor size", kind: "select", options: boxAwgOptions, default: "12" },
      { key: "conductorCount", label: "Insulated conductors", kind: "number", default: "6", step: "1" },
      { key: "deviceCount", label: "Devices (yokes)", kind: "number", default: "1", step: "1" },
      {
        key: "internalClamps",
        label: "Internal clamps",
        kind: "select",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        default: "yes",
      },
      { key: "groundCount", label: "Grounding conductors", kind: "number", default: "2", step: "1" },
    ],
    compute: (v) => {
      const awg = v.awg as never;
      const r = boxFill({
        boxVolumeIn3: num(v.boxVolumeIn3),
        conductors: [{ awg, count: num(v.conductorCount) }],
        deviceCount: num(v.deviceCount),
        internalClamps: v.internalClamps === "yes",
        supportFittings: 0,
        groundingConductors: num(v.groundCount) > 0 ? [{ awg, count: num(v.groundCount) }] : [],
      });
      if (!r.ok) return r;
      return {
        ok: true,
        headline: {
          label: "Required volume",
          value: `${fmt(r.value.requiredVolumeIn3)} in³`,
          warn: r.value.overfilled,
        },
        rows: [
          { label: "Box volume", value: `${fmt(r.value.boxVolumeIn3)} in³` },
          { label: "Remaining", value: `${fmt(r.value.remainingVolumeIn3)} in³`, warn: r.value.overfilled },
          {
            label: "Status",
            value: r.value.overfilled ? "OVER FILL — bigger box" : "Within NEC 314.16",
            warn: r.value.overfilled,
          },
        ],
      };
    },
  },

  conduitBending: {
    fields: [
      {
        key: "angleDeg",
        label: "Offset angle",
        kind: "select",
        options: OFFSET_ANGLES.map((a) => ({ value: String(a), label: `${a}°` })),
        default: "30",
      },
      { key: "offsetDepthIn", label: "Offset depth", unit: "in", kind: "number", default: "6" },
    ],
    compute: (v) => {
      const r = conduitBending({
        kind: "offset",
        angleDeg: num(v.angleDeg) as never,
        offsetDepthIn: num(v.offsetDepthIn),
      });
      if (!r.ok) return r;
      // Input is always kind "offset", so the result is an offset result.
      const offset = r.value as Extract<typeof r.value, { kind: "offset" }>;
      return {
        ok: true,
        headline: { label: "Distance between bends", value: `${fmt(offset.distanceBetweenBendsIn)} in` },
        rows: [{ label: "Shrink", value: `${fmt(offset.shrinkIn, 3)} in` }],
      };
    },
  },
};

/** Default form values for a calculator (keyed field → default string). */
export function defaultValues(id: CalculatorId): Record<string, string> {
  return Object.fromEntries(CALC_FORMS[id].fields.map((f) => [f.key, f.default]));
}
